import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import { getToken } from '../../services/tokenStore';

const API_BASE_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;

// See PdfJsHighlightViewer.jsx for why this is conditional on same-origin:
// pdfUrl is sometimes our own auth-gated /api/documents/:id/pdf and sometimes
// a direct Azure Blob SAS URL, which authenticates via its query string, not
// a header — sending Authorization there risks a CORS preflight failure.
const isOwnBackendUrl = (url) => typeof url === 'string' && url.startsWith(API_BASE_URL);

// See PdfJsHighlightViewer.jsx for why this is a one-time auto-reload:
// a stale open tab can reference a hashed worker filename a newer deploy
// no longer serves, and pdf.js's fake-worker fallback surfaces that 404
// as a dynamic-import failure. Guarded by sessionStorage against reload-looping.
const isStaleAssetError = (err) =>
  /dynamically imported module|setting up fake worker/i.test(err?.message || '');

// See PdfJsHighlightViewer.jsx for why this isn't `new URL('pdfjs-dist/...',
// import.meta.url)`: react-scripts' Babel pass over bundled .mjs files
// injects absolute-filesystem-path helper imports that break once emitted
// as a standalone asset. scripts/copy-pdf-worker.js (postinstall) copies
// the worker into public/ untouched instead.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const PdfJsSimpleViewer = ({ pdfUrl }) => {
  const [numPages, setNumPages] = useState(0);
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadError, setLoadError] = useState(null);
  const pageRefs = useRef({});

  useEffect(() => {
    const loadPdf = async () => {
      const token = getToken();
      const loadingTask = pdfjsLib.getDocument({
        url: pdfUrl,
        isEvalSupported: false,
        httpHeaders: token && isOwnBackendUrl(pdfUrl) ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const loadedPdf = await loadingTask.promise;
      setPdf(loadedPdf);
      setNumPages(loadedPdf.numPages);
      setLoadError(null);
    };
    if (pdfUrl) {
      loadPdf().catch((err) => {
        console.error('[PdfJsSimpleViewer] Failed to load PDF:', err);
        const reloadKey = 'pdf_stale_asset_reload_attempted';
        if (isStaleAssetError(err) && !sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
          return;
        }
        setLoadError('Failed to load PDF. Please try reloading the page.');
      });
    }
  }, [pdfUrl]);

  return (
    <div style={{ width: '100%', maxHeight: 900, overflow: 'auto', background: '#eee', padding: 16 }}>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage <= 1}
        >
          Previous
        </button>
        <span>Page {currentPage} of {numPages}</span>
        <button
          onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
          disabled={currentPage >= numPages}
        >
          Next
        </button>
      </div>
      {loadError && (
        <div style={{ padding: 16, color: '#b00020' }}>
          {loadError}{' '}
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      )}
      {numPages > 0 && (
        <PDFPage
          key={currentPage}
          pdf={pdf}
          pageNum={currentPage}
          pageRefs={pageRefs}
        />
      )}
    </div>
  );
};

const PDFPage = ({ pdf, pageNum, pageRefs }) => {
  const [viewport, setViewport] = useState(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const canvasRef = useRef();
  const renderTaskRef = useRef(null);

  useEffect(() => {
    if (renderTaskRef.current && renderTaskRef.current.cancel) {
      renderTaskRef.current.cancel();
    }
    const renderPage = async () => {
      if (!pdf) return;
      const page = await pdf.getPage(pageNum);
      const scale = 1.5;
      const vp = page.getViewport({ scale });
      setViewport(vp);
      setPageWidth(vp.width);
      setPageHeight(vp.height);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = vp.width;
      canvas.height = vp.height;
      renderTaskRef.current = page.render({ canvasContext: context, viewport: vp });
      try {
        await renderTaskRef.current.promise;
      } catch (e) {}
    };
    renderPage();
    return () => {
      if (renderTaskRef.current && renderTaskRef.current.cancel) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pdf, pageNum]);

  return (
    <div
      ref={el => (pageRefs.current[pageNum] = el)}
      style={{
        position: 'relative',
        margin: '24px auto',
        background: '#fff',
        width: pageWidth,
        height: pageHeight,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
};

export default PdfJsSimpleViewer; 